import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { focusOutline } from '@monorepo/shared/styles/focus';
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import * as xlsx from 'xlsx';
import { SheetTable } from './SheetTable';
import { SheetTabs } from './SheetTabs';
import { StyledPreviewMessage } from './styled';
import { SpreadsheetSheet } from './types';

const MAX_PREVIEW_ROWS = 200;

const StyledPreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledSheetPanel = styled.div`
  flex: 1;
  overflow: auto;

  &:focus-visible {
    ${focusOutline()}
  }
`;

async function fetchSpreadsheetSheets(
  _key: string,
  src: string,
): Promise<SpreadsheetSheet[]> {
  const response = await fetch(src);
  if (!response.ok) {
    throw new Error(`Failed to fetch spreadsheet: ${response.status}`);
  }
  const workbook = xlsx.read(await response.arrayBuffer(), {
    type: 'array',
    // Parse one extra row per sheet so we can tell whether a sheet was cut off.
    sheetRows: MAX_PREVIEW_ROWS + 1,
  });
  return workbook.SheetNames.map((name) => {
    const sheet = workbook.Sheets[name];
    const rows = sheet
      ? xlsx.utils.sheet_to_json<string[]>(sheet, {
          header: 1,
          defval: '',
          raw: false,
        })
      : [];
    return {
      name,
      rows: rows.slice(0, MAX_PREVIEW_ROWS),
      isTruncated: rows.length > MAX_PREVIEW_ROWS,
    };
  });
}

type SpreadsheetPreviewProps = {
  src: string;
  title?: string;
};

/** Renders an xlsx, xls, or csv file as a table, with tabs when the workbook has multiple sheets. */
export function SpreadsheetPreview({ src, title }: SpreadsheetPreviewProps) {
  const [idPrefix] = useState(() => `spreadsheet-preview-${uuidv4()}`);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const {
    data: sheets,
    isLoading,
    isError,
  } = useQuery(['spreadsheetPreview', src], fetchSpreadsheetSheets, {
    staleTime: Infinity,
    retry: false,
  });

  if (isLoading) return <Loading />;

  if (isError || !sheets?.length) {
    return (
      <StyledPreviewMessage>
        This file could not be previewed.
      </StyledPreviewMessage>
    );
  }

  const selectedSheet = sheets[selectedIndex];
  if (!selectedSheet) return null;
  const hasMultipleSheets = sheets.length > 1;

  return (
    <StyledPreviewContainer>
      <StyledSheetPanel
        id={`${idPrefix}-panel`}
        tabIndex={0}
        role={hasMultipleSheets ? 'tabpanel' : 'region'}
        aria-label={
          hasMultipleSheets
            ? undefined
            : title ?? `Spreadsheet preview, ${selectedSheet.name}`
        }
        aria-labelledby={
          hasMultipleSheets ? `${idPrefix}-tab-${selectedIndex}` : undefined
        }
      >
        <SheetTable
          sheet={selectedSheet}
          ariaLabel={
            hasMultipleSheets ? selectedSheet.name : title ?? selectedSheet.name
          }
        />
      </StyledSheetPanel>
      {hasMultipleSheets && (
        <SheetTabs
          idPrefix={idPrefix}
          sheetNames={sheets.map((sheet) => sheet.name)}
          selectedIndex={selectedIndex}
          onSelect={setSelectedIndex}
        />
      )}
    </StyledPreviewContainer>
  );
}
